// Mirror of the belegleser output contract (schema.py::ExtractionResult, v3).
// Backend field names stay snake_case verbatim; business values are strings or
// null — real numbers only for coordinates, pages, confidences and delta.

/** `[x0, y0, x1, y1]` in PDF points, origin top-left, upright frame. */
export type Bbox = [number, number, number, number];

export const SUPPORTED_EXTRACTION_SCHEMA_VERSION = 3;

export type ItemType = 'standard' | 'alternative' | 'optional' | 'service' | 'lump_sum';

export type AdjustmentKind = 'percent' | 'amount' | 'rate';
export type AdjustmentCategory = 'discount' | 'surcharge' | 'cash_discount' | 'interest';
export type AdjustmentBase = 'subtotal' | 'previous' | 'gross';

/**
 * One deduction/surcharge of an item level. List order is calculation order
 * (chained discounts). `kind === null` means read but not proven by arithmetic.
 */
export interface Adjustment {
  value: string | null;
  kind: AdjustmentKind | null;
  category: AdjustmentCategory | null;
  base: AdjustmentBase | null;
  /** ISO-8601 duration ("P14D") or ISO date — only for cash_discount/interest. */
  days: string | null;
}

export interface ItemValidation {
  arithmetic_ok: boolean | null;
  delta: number | null;
  confidence: number;
}

/**
 * One level of the item tree. `items[0]` of the result is ALWAYS the root item
 * carrying the document totals; the document positions are its `items`.
 */
export interface ExtractionItem {
  item_type: ItemType | null;
  item_number: string | null;
  description: string | null;
  product_number: string | null;
  ean: string | null;
  quantity: string | null;
  unit: string | null;
  unit_price: string | null;
  discounts: Adjustment[];
  vat_rate: string | null;
  vat: string | null;
  net: string | null;
  gross: string | null;
  currency: string | null;
  page: number | null;
  bbox: Bbox | null;
  /** Per-field box of THIS row: attribute name → bbox (page lives in `page`). */
  field_bboxes: Record<string, Bbox>;
  items: ExtractionItem[];
  validation: ItemValidation;
  sum_matches_net: boolean | null;
  sum_matches_gross: boolean | null;
  vat_consistent: boolean | null;
}

export interface SupplierMetadata {
  name: string | null;
  address: string | null;
  uid: string | null;
  gln: string | null;
}

export interface RecipientMetadata {
  address_billing: string | null;
  gln_billing: string | null;
  address_delivery: string | null;
  gln_delivery: string | null;
}

export interface ReceiptMetadata {
  receipt_number: string | null;
  receipt_date: string | null;
  valid_until: string | null;
  customer_number: string | null;
  project_name: string | null;
  billing_period: string | null;
  delivery_note_number: string | null;
  net_days: string | null;
}

export interface ExtractionMetadata {
  supplier: SupplierMetadata;
  recipient: RecipientMetadata;
  receipt: ReceiptMetadata;
  emails: string[];
}

export interface SourceInfo {
  file_name: string | null;
  extraction_method: string | null;
  confidence: number;
  warnings: string[];
}

export interface DocumentTypeInfo {
  document_type: string | null;
  confidence: number;
  evidence_text: string | null;
  evidence_bbox: Bbox | null;
  page: number | null;
}

export interface PipelineMeta {
  ingest_module: string | null;
  layout_module: string | null;
  position_module: string | null;
  header_module: string | null;
  layer_statuses: Record<string, 'ok' | 'degraded' | 'failed' | null>;
}

export interface SegmentInfo {
  segment_type: string | null;
  role: string | null;
  page: number | null;
  bbox: Bbox | null;
  confidence: number;
  role_confidence: number;
  text: string;
}

export interface PageRotation {
  page: number | null;
  /** Measured clockwise rotation in degrees; the 90°-rounded part is already applied. */
  rotation: number;
}

export interface TraceEntry {
  layer: string | null;
  chosen: string | null;
  status: 'ok' | 'degraded' | 'failed' | null;
  input_summary: string | null;
  confidence: number | null;
}

export interface PostprocessInfo {
  version: number;
  /** Original document text per RESULT PATH ("items[0].items[3].quantity"). */
  raw_values: Record<string, string>;
  address_parts: Record<string, unknown>;
}

export interface ExtractionResult {
  schema_version: number;
  metadata: ExtractionMetadata;
  /** Exactly ONE entry: the root item (totals); positions are its children. */
  items: ExtractionItem[];
  source: SourceInfo;
  document_type: DocumentTypeInfo | null;
  /** Root field boxes: full result path → [page, bbox]. */
  field_bboxes: Record<string, [number, Bbox]>;
  pipeline_meta: PipelineMeta | null;
  segments: SegmentInfo[];
  pages: PageRotation[];
  traces: TraceEntry[];
  postprocess: PostprocessInfo | null;
}

/** A result is readable only in the exact schema version this module mirrors. */
export function isSupportedExtractionResult(
  result: Pick<ExtractionResult, 'schema_version'> | null | undefined
): result is ExtractionResult {
  return result?.schema_version === SUPPORTED_EXTRACTION_SCHEMA_VERSION;
}

/** The root item (`items[0]`) carrying the document totals. */
export function rootItem(result: ExtractionResult): ExtractionItem | undefined {
  return result.items[0];
}

export interface FlatItem {
  item: ExtractionItem;
  /** Result path of the row ("items[0].items[2].items[0]") — the raw_values key prefix. */
  path: string;
  depth: number;
}

/**
 * All document positions as a flat pre-order list (parent before children),
 * excluding the totals root. Display order of the position list.
 */
export function flattenItems(result: ExtractionResult): FlatItem[] {
  const out: FlatItem[] = [];
  const walk = (items: ExtractionItem[], path: string, depth: number): void => {
    items.forEach((item, i) => {
      const itemPath = `${path}.items[${i}]`;
      out.push({ item, path: itemPath, depth });
      walk(item.items, itemPath, depth + 1);
    });
  };
  walk(rootItem(result)?.items ?? [], 'items[0]', 0);
  return out;
}
